import { encodePeopleGroupIds } from "@app/lib/api/people/group_key";
import type { AppRouter } from "@app/lib/platform";

export const setQueryParam = (
  router: AppRouter,
  key: string,
  value: string
) => {
  const q = router.query;
  q[key] = value;

  // Preserve the hash when updating query params
  const hash = window.location.hash;

  void router
    .push(
      {
        pathname: router.pathname,
        query: q,
      },
      undefined,
      { shallow: true }
    )
    .then(() => {
      // Restore hash after router.push (Next.js doesn't preserve it)
      if (hash && window.location.hash !== hash) {
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.search}${hash}`
        );
      }
    });
};

export const parseQueryString = (url: string) => {
  // Remove everything before the query string
  const queryString = url.split("?")[1] || "";
  const searchParams = new URLSearchParams(queryString);

  // Convert to plain object
  const params: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
};

export const getAgentBuilderRoute = (
  workspaceId: string,
  route: string,
  queryParams?: string
): string => {
  const basePath = "agents";
  const fullPath = `/w/${workspaceId}/builder/${basePath}${route === "manage" ? "" : `/${route}`}`;
  return queryParams ? `${fullPath}?${queryParams}` : fullPath;
};

export const getSkillBuilderRoute = (
  workspaceId: string,
  route: string,
  queryParams?: string
): string => {
  const basePath = "skills";
  const fullPath = `/w/${workspaceId}/builder/${basePath}${route === "manage" ? "" : `/${route}`}`;
  return queryParams ? `${fullPath}?${queryParams}` : fullPath;
};

export const getManageSkillsRoute = (workspaceId: string, skillId?: string) => {
  return (
    `/w/${workspaceId}/builder/skills` + (skillId ? `#?skillId=${skillId}` : "")
  );
};

export const getConversationRoute = (
  workspaceId: string,
  conversationIdOrNew: string | null = "new",
  queryParams?: string,
  baseUrl?: string
): string => {
  const conversationId = conversationIdOrNew ?? "new";
  const fullPath = `/w/${workspaceId}/conversation/${conversationId}`;
  const route = queryParams ? `${fullPath}?${queryParams}` : fullPath;
  return baseUrl ? `${baseUrl}${route}` : route;
};

export const getLiveDocPageRoute = (workspaceId: string, docId: string) =>
  `/w/${workspaceId}/live/${docId}`;

export const getSpaceRoute = (workspaceId: string, spaceId: string) => {
  return `/w/${workspaceId}/spaces/${spaceId}`;
};

export const getHomeRoute = (workspaceId: string) => {
  return `/w/${workspaceId}/home`;
};

export const getTasksRoute = (workspaceId: string) => {
  return `/w/${workspaceId}/tasks`;
};

export const getLibraryRoute = (workspaceId: string, fileId?: string) => {
  return `/w/${workspaceId}/library${fileId ? `/${fileId}` : ""}`;
};

export const getDashboardsRoute = (workspaceId: string, fileId?: string) => {
  return `/w/${workspaceId}/library/dashboards${fileId ? `/${fileId}` : ""}`;
};

// The wiki lives on Contacts: a person's page opens their card, and every
// other page opens as a tab of the viewer's own card. `/wiki/*` redirects here.
export const getWikiRoute = (workspaceId: string, slug?: string) => {
  return `/w/${workspaceId}/people?view=people&slug=${encodeURIComponent(slug ?? "overview")}`;
};

export const getWikiChangelogRoute = (workspaceId: string) => {
  return getWikiRoute(workspaceId, "recent-updates");
};

export const getWikiMemoryMapRoute = (workspaceId: string) => {
  return getWikiRoute(workspaceId, "memory-map");
};

export const getRoutinesRoute = (workspaceId: string) => {
  return `/w/${workspaceId}/routines`;
};

export const getRoutineRoute = (workspaceId: string, routineId: string) => {
  return `/w/${workspaceId}/routines/${routineId}`;
};

export const getPeopleRoute = (workspaceId: string) => {
  return `/w/${workspaceId}/people`;
};

// A People group is identified by its members, not by a stored id, so the URL
// has to be canonical or the same group would cache and revalidate twice.
export const getPeopleConversationsRoute = (
  workspaceId: string,
  userIds: string[]
) => {
  const segment = encodePeopleGroupIds(userIds)
    .split(",")
    .map(encodeURIComponent)
    .join(",");
  return `/w/${workspaceId}/people/conversations/${segment}`;
};

export const getChatsRoute = (workspaceId: string) => `/w/${workspaceId}/chats`;

export const getChatRoute = (
  workspaceId: string,
  chatId: string,
  segmentConversationId?: string | null
) => {
  const path = `/w/${workspaceId}/chats/${encodeURIComponent(chatId)}`;
  return segmentConversationId
    ? `${path}?segment=${encodeURIComponent(segmentConversationId)}`
    : path;
};

export const getPodsRoute = (workspaceId: string) => {
  return `/w/${workspaceId}/pods`;
};

export const getPodRoute = (workspaceId: string, spaceId: string) => {
  return `/w/${workspaceId}/pods/${spaceId}`;
};

export const getPodConversationRoute = (
  workspaceId: string,
  spaceId: string,
  conversationId: string
) => {
  return `${getPodRoute(workspaceId, spaceId)}/conversation/${conversationId}`;
};
